<template>
    <el-popover placement="bottom-start" width="600" trigger="click" class="px-0" ref="pendingTasksPopover" :teleport="false">
        <template #reference>
            <el-menu-item index="pendingTasksMenu" :route="{}">
                <el-badge v-if="!loadingTasks && pendingTasks?.length" :offset="[0, 15]" :value="pendingTasks.length"><el-icon><Finished /></el-icon>Tasks</el-badge>
                <span v-else><el-icon><Finished /></el-icon>Tasks</span>
            </el-menu-item>
        </template>
        <el-skeleton animated v-if="loadingTasks">
            <el-skeleton-item variant="p" />
            <el-skeleton-item variant="h1" />
            <el-skeleton-item variant="h1" />
            <el-skeleton-item variant="rect" />
        </el-skeleton>
        <div class="px-0 py-2" v-else-if="pendingTasks?.length">
            <el-text class="mx-4 mb-4 d-block">You have <el-text type="danger" tag="b">{{ pendingTasks.length }}</el-text> pending task(s). You can filter these tasks based on their category from below. Clicking on any pending task will take you to the relevant CARPORT page.</el-text>
            <div class="d-flex justify-content-center my-4">
                <el-checkbox-group v-model="tasksFilter">
                    <el-checkbox size="large" value="facility" v-if="userFacilityTasksCount > 0">Facility ({{ userFacilityTasksCount }})</el-checkbox>
                    <el-checkbox size="large" value="acceptance" v-if="userAcceptanceTasksCount > 0">Acceptance ({{ userAcceptanceTasksCount }})</el-checkbox>
                    <el-checkbox size="large" value="quality_assurance" v-if="userQualityAssuranceTasksCount > 0">Quality Assurance ({{ userQualityAssuranceTasksCount }})</el-checkbox>
                    <el-checkbox size="large" value="scc_configurations" v-if="userSCCConfigurationsTasksCount > 0">SCC Configurations ({{ userSCCConfigurationsTasksCount }})</el-checkbox>
                </el-checkbox-group>
            </div>
            <el-scrollbar height="400px" wrap-class="shadow-scroll" v-if="filteredPendingTasks?.length">
                <div v-for="(task, index) in filteredPendingTasks" :key="index" @click="() => { $router.push({path: '/' + task.action.replace(/^(?:\/\/|[^/]+)*\//, '')}); closeTasksMenu() }">
                    <div class="d-flex flex-nowrap task align-items-center">
                        <div style="width: 32px; height: 32px;" class="flex-shrink-0 align-items-center align-middle justify-content-center mx-4 my-4">
                            <template v-if="['complete_facility_profile'].includes(task.type)">
                                <OfficeBuilding />
                            </template>
                            <template v-else-if="['submit_acceptance_application', 'upload_upgrade_plan'].includes(task.type)">
                                <Document />
                            </template>
                            <template v-else-if="['finish_lidar_feasibility_review', 'finish_lidar_feature_review', 'finish_lidar_channel_review', 'finish_lidar_data_review', 'finish_lidar_general_review', 'finish_photometer_data_review', 'finish_photometer_general_review'].includes(task.type)">
                                <List />
                            </template>
                            <template v-else-if="['submit_acceptance'].includes(task.type)">
                                <Checked />
                            </template>
                            <template v-else-if="['set_up_lidar_qa'].includes(task.type)">
                                <SetUp />
                            </template>
                            <template v-else-if="['submit_qa_measurements', 'accept_reject_qa_measurements'].includes(task.type)">
                                <FolderOpened />
                            </template>
                            <template v-else-if="['issue_preliminary_qa_report', 'accept_reject_qa_report'].includes(task.type)">
                                <FolderOpened />
                            </template>
                            <template v-else-if="['confirm_scc_configurations'].includes(task.type)">
                                <Management />
                            </template>
                        </div>
                        <div class="flex-grow-1 align-items-center pe-4 py-4">
                            <el-text size="small" type="info">{{ $date(task.since) }}</el-text>
                            <br />
                            <el-text size="large" class="title fw-bold">
                                <template v-if="task.type === 'complete_facility_profile'">Complete facility profile</template>
                                <template v-else-if="task.type === 'submit_acceptance_application'">Submit application</template>
                                <template v-else-if="task.type === 'upload_upgrade_plan'">Upload upgrade plan</template>
                                <template v-else-if="['finish_lidar_feasibility_review', 'finish_lidar_feature_review', 'finish_lidar_channel_review', 'finish_lidar_data_review', 'finish_lidar_general_review', 'finish_photometer_data_review', 'finish_photometer_general_review'].includes(task.type)">Submit review</template>
                                <template v-else-if="task.type === 'submit_acceptance'">Set acceptance</template>
                                <template v-else-if="task.type === 'set_up_lidar_qa'">Select operational configurations</template>
                                <template v-else-if="task.type === 'submit_qa_measurements'">Upload measurements</template>
                                <template v-else-if="task.type === 'accept_reject_qa_measurements'">Accept or reject measurements</template>
                                <template v-else-if="task.type === 'issue_preliminary_qa_report'">Issue QA report</template>
                                <template v-else-if="task.type === 'accept_reject_qa_report'">Accept or reject QA report</template>
                                <template v-else-if="task.type === 'confirm_scc_configurations'">Confirm configuration data</template>
                                <template v-else>{{ task.type }}</template>
                            </el-text>
                            <br />
                            <el-text class="title">
                                {{ task.description }}
                            </el-text>
                        </div>
                    </div>
                    <el-divider v-if="index !== pendingTasks.length - 1" class="my-0" />
                </div>
            </el-scrollbar>
            <el-empty v-else description="No such pending tasks" />
            <div class="d-flex justify-content-center mt-3">
                <el-button icon="Refresh" size="large" type="text" @click="reloadPendingTasks">Reload pending tasks</el-button>
            </div>
        </div>
        <el-result v-else icon="success" title="All done!" sub-title="You currently have no pending tasks in CARPORT">
            <template #extra>
                <el-button type="text" size="large" icon="Refresh" @click="fetchPendingTasks">Check again</el-button>
            </template>
        </el-result>
    </el-popover>
</template>

<script>
import {mapGetters} from 'vuex'

import api from '@/lib/api'

const FACILITY_TASKS = ["complete_facility_profile", "submit_acceptance_application"]
const ACCEPTANCE_TASKS = [
    "finish_lidar_feasibility_review", "finish_lidar_feature_review",
    "finish_lidar_channel_review", "finish_lidar_data_review", "finish_lidar_general_review",
    "finish_photometer_data_review", "finish_photometer_general_review", "upload_upgrade_plan",
    "submit_acceptance"
]
const QUALITY_ASSURANCE_TASKS = [
    "set_up_lidar_qa", "submit_qa_measurements","accept_reject_qa_measurements",
    "issue_preliminary_qa_report", "accept_reject_qa_report"
]
const SCC_CONFIGURATIONS_TASKS = ["confirm_scc_configurations"]

export default {
    data: () => ({
        pendingTasks: [],
        loadingTasks: false,
        tasksFilter: [],
        fetchCallback: undefined
    }),

    computed: {
        ...mapGetters('auth', ['isLoggedIn', 'userinfo']),

        filteredPendingTasks () {
            if (!this.tasksFilter?.length) {
                return this.pendingTasks
            }

            return this.pendingTasks.filter(task => {

                if (this.tasksFilter.includes("facility") && FACILITY_TASKS.includes(task.type)) { return true }
                if (this.tasksFilter.includes("acceptance") && ACCEPTANCE_TASKS.includes(task.type)) { return true }
                if (this.tasksFilter.includes("quality_assurance") && QUALITY_ASSURANCE_TASKS.includes(task.type)) { return true }
                if (this.tasksFilter.includes("scc_configurations") && SCC_CONFIGURATIONS_TASKS.includes(task.type)) { return true }

                return false
            })
        },

        userFacilityTasksCount () { return this.pendingTasks.filter(task => FACILITY_TASKS.includes(task.type)).length },
        userAcceptanceTasksCount () { return this.pendingTasks.filter(task => ACCEPTANCE_TASKS.includes(task.type)).length },
        userQualityAssuranceTasksCount () { return this.pendingTasks.filter(task => QUALITY_ASSURANCE_TASKS.includes(task.type)).length },
        userSCCConfigurationsTasksCount () { return this.pendingTasks.filter(task => SCC_CONFIGURATIONS_TASKS.includes(task.type)).length }
    },

    watch: {
        isLoggedIn () { this.fetchPendingTasks() }
    },

    methods: {
        fetchPendingTasks () {
            if (!this.isLoggedIn) {
                // Empty pending tasks queue if user is not logged in
                this.pendingTasks = []
                return
            }

            this.loadingTasks = true
            this.pendingTasks = []

            api.tasks.checkPending ()
            .then (response => {
                this.pendingTasks = response.data
            })
            .catch(error => {
                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Error checking pending tasks: ${err}`,
                    type: 'error'
                })
            })
            .finally (() => {
                this.loadingTasks = false
            })
        },
        reloadPendingTasks () {
            this.fetchPendingTasks ()
            this.tasksFilter = []
        },
        closeTasksMenu () {
            // this.$refs.pendingTasksPopover?.hide()
        },
    },

    mounted () {
        this.fetchPendingTasks()

        window.setInterval (this.fetchPendingTasks, 60000)
    }
}
</script>

<style scoped>
.task {
    color: var(--el-color-info-dark-2);
    /* background-color: white; */
    cursor: pointer;
}

.task:hover {
    background-color: var(--el-color-primary-light-8);
    color: var(--el-color-primary)
}

.task:hover .el-text.title {
    color: var(--el-color-primary)
}
</style>

<style>
.shadow-scroll {
    background:
    /* Shadow Cover TOP */
    linear-gradient(
      white 30%,
      rgba(255, 255, 255, 0)
    ) center top,
    
    /* Shadow Cover BOTTOM */
    linear-gradient(
      rgba(255, 255, 255, 0), 
      white 70%
    ) center bottom,
    
    /* Shadow TOP */
    radial-gradient(
      farthest-side at 50% 0,
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0)
    ) center top,
    
    /* Shadow BOTTOM */
    radial-gradient(
      farthest-side at 50% 100%,
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0)
    ) center bottom;
  
  background-repeat: no-repeat;
  background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
  background-attachment: local, local, scroll, scroll;
}
</style>